import React, { useState } from "react";
import { Card } from "views/components/ui/Card";
import { Upload, Plus, X, Loader2 } from "lucide-react";

const BatchToolSubmit = ({ onSubmit }) => {
    const [tools, setTools] = useState([{ name: "", externalUrl: "" }]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleAddTool = () => {
        setTools([...tools, { name: "", externalUrl: "" }]);
    };

    const handleRemoveTool = (index) => {
        setTools(tools.filter((_, i) => i !== index));
    };

    const handleToolChange = (index, field, value) => {
        const newTools = [...tools];
        newTools[index] = { ...newTools[index], [field]: value };
        setTools(newTools);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validate required fields
        const invalidTools = tools.filter(
            (tool) => !tool.name || !tool.externalUrl
        );

        if (invalidTools.length > 0) {
            setError("Please fill in both name and URL for each tool");
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(tools);
            // Reset form after successful submission
            setTools([{ name: "", externalUrl: "" }]);
        } catch (err) {
            setError(err.message || "Failed to submit tools");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Card>
            <div className="p-6">
                <h2 className="text-lg font-semibold mb-4">
                    Batch Submit Tools
                </h2>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Tools List */}
                    <div className="space-y-4">
                        {tools.map((tool, index) => (
                            <div
                                key={index}
                                className="border rounded-lg p-4 bg-white"
                            >
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-medium text-gray-900">
                                        Tool {index + 1}
                                    </h3>
                                    {tools.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleRemoveTool(index)
                                            }
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={tool.name}
                                            onChange={(e) =>
                                                handleToolChange(
                                                    index,
                                                    "name",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2 border rounded-md"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            URL *
                                        </label>
                                        <input
                                            type="url"
                                            value={tool.externalUrl}
                                            onChange={(e) =>
                                                handleToolChange(
                                                    index,
                                                    "externalUrl",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2 border rounded-md"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add Tool Button */}
                    <button
                        type="button"
                        onClick={handleAddTool}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Another Tool
                    </button>

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Submit Tools
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </Card>
    );
};

export default BatchToolSubmit;
